import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import DeleteButton from '../components/DeleteButton';
import ImageViewer from '../components/ImageViewer';
import { locationLabel } from '../utils/labels';
import './QuickAdd.css';

// Type item names one after another while looking at the compartment's picture.
// New items go to the top of the list, newest first; Done returns to the compartment.
function QuickAdd() {
  const { id } = useParams();
  const navigate = useNavigate();
  const inputRef = useRef(null);
  const [location, setLocation] = useState(null);
  const [items, setItems] = useState([]);
  const [name, setName] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState('loading'); // loading | ready | missing

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const [locRes, itemsRes] = await Promise.all([
          fetch(`/api/locations/${id}`),
          fetch(`/api/locations/${id}/items?quick=1`)
        ]);
        if (!locRes.ok || !itemsRes.ok) throw new Error('missing');
        const [loc, list] = await Promise.all([locRes.json(), itemsRes.json()]);
        if (cancelled) return;
        setLocation(loc);
        setItems(list);
        setStatus('ready');
      } catch (e) {
        if (!cancelled) setStatus('missing');
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [id]);

  const addItem = async (event) => {
    event.preventDefault();
    const trimmed = name.trim();
    if (!trimmed || busy) return;
    setBusy(true);
    setError('');
    try {
      const response = await fetch('/api/items', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: trimmed, storageLocationId: id })
      });
      if (response.ok) {
        const created = await response.json();
        setItems((prev) => [{ id: created.id, name: created.name, createdAt: created.createdAt }, ...prev]);
        setName('');
      } else {
        const data = await response.json().catch(() => ({}));
        setError(data.error || `Could not add the item (${response.status})`);
      }
    } catch (e) {
      setError('Could not reach the server. If you were signed out, reload the page and sign in again.');
    }
    setBusy(false);
    if (inputRef.current) inputRef.current.focus();
  };

  if (status === 'loading') {
    return <div className="quick-add"><p>Loading...</p></div>;
  }
  if (status === 'missing') {
    return <div className="quick-add"><p>This place was not found.</p></div>;
  }

  const label = locationLabel(location).toLowerCase();

  return (
    <div className="quick-add">
      <div className="quick-add-top">
        <div>
          <div className="quick-add-kind">Adding items to this {label}</div>
          <h1>{location.name}</h1>
        </div>
        <button type="button" className="btn btn-primary" onClick={() => navigate(`/locations/${id}`)}>
          Done
        </button>
      </div>

      {location.image ? (
        <div className="quick-add-picture">
          <ImageViewer
            src={`data:${location.imageMimeType};base64,${location.image}`}
            alt={`Picture of ${location.name}`}
            className="quick-add-image"
          />
          <span className="quick-add-hint">Tap the picture to enlarge it</span>
        </div>
      ) : (
        <p className="quick-add-nopicture">This {label} has no picture. You can still add items below.</p>
      )}

      <form className="quick-add-form" onSubmit={addItem}>
        <input
          ref={inputRef}
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Item name"
          aria-label="Item name"
          autoFocus
          autoComplete="off"
          autoCapitalize="sentences"
          enterKeyHint="done"
        />
        <button type="submit" className="btn btn-primary" disabled={busy || !name.trim()}>
          Add
        </button>
      </form>
      {error && <p className="quick-add-error">{error}</p>}

      <h2 className="quick-add-count">{items.length === 0 ? 'No items yet' : `${items.length} item${items.length === 1 ? '' : 's'}`}</h2>
      <ul className="quick-add-list">
        {items.map((item) => (
          <li key={item.id}>
            <span className="quick-add-name">{item.name}</span>
            <DeleteButton
              url={`/api/items/${item.id}`}
              noun="item"
              className="quick-add-remove"
              onDeleted={() => setItems((prev) => prev.filter((x) => x.id !== item.id))}
            >
              ✕
            </DeleteButton>
          </li>
        ))}
      </ul>
    </div>
  );
}

export default QuickAdd;
